import { useEffect, useMemo, useState } from 'react';
import Avatar from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import { api, apiList } from '@/utils/api';
import type { SinglePayee } from '@/types/portal';

interface WalletAccountStepProps {
  value: string;
  onChange: (value: string) => void;
  payees: SinglePayee[];
  onPickPayee: (payee: SinglePayee) => void;
  /** A wallet found by typing its ID in the search: whose it is, already checked. */
  onPickWallet: (walletId: string, name: string) => void;
  onContinue: () => void;
  canContinue: boolean;
  /** While the wallet ID is being looked up (POST /payments/resolve). */
  checking?: boolean;
  /** Why the ID can't be used, e.g. no Twiva wallet has it. */
  error?: string;
}

/**
 * Wallet-to-wallet: find the creator by name or email (searched as you type among the
 * agency's creators with a Twiva Pay wallet), or enter a Twiva wallet ID directly.
 */
export default function WalletAccountStep({
  value,
  onChange,
  payees,
  onPickPayee,
  onPickWallet,
  onContinue,
  canContinue,
  checking = false,
  error = '',
}: WalletAccountStepProps) {
  const [search, setSearch] = useState('');
  const text = search.trim();
  const typedId = /^\d{4,}$/.test(text) ? text : '';

  // Saved payees match straight away.
  const saved = useMemo(() => {
    const lower = text.toLowerCase();
    if (lower.length < 2) return [];
    return payees.filter(
      (item) =>
        item.target.walletId &&
        (item.name.toLowerCase().includes(lower) ||
          (item.handle ?? '').toLowerCase().includes(lower) ||
          (item.target.walletId ?? '').includes(lower)),
    );
  }, [text, payees]);

  // Creators are searched on the server as you type (GET /payees/wallet-holders?q=).
  const [creators, setCreators] = useState<SinglePayee[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState('');
  useEffect(() => {
    setCreators([]);
    setSearched('');
    if (text.length < 2 || typedId) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      setSearching(true);
      apiList<SinglePayee>(`/payees/wallet-holders?q=${encodeURIComponent(text)}`)
        .then(({ data }) => !cancelled && setCreators(data))
        .catch(() => !cancelled && setCreators([]))
        .finally(() => {
          if (cancelled) return;
          setSearching(false);
          setSearched(text);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      setSearching(false);
    };
  }, [text, typedId]);

  const results = useMemo(() => {
    const seen = new Set<string>();
    return [...saved, ...creators]
      .filter((item) => {
        const id = item.target.walletId ?? '';
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .slice(0, 8);
  }, [saved, creators]);

  // A wallet ID typed in the search is looked up (POST /payments/resolve), so holders
  // who aren't the agency's creators can still be found.
  const [found, setFound] = useState<{ walletId: string; name: string | null; exists: boolean } | null>(null);
  const [looking, setLooking] = useState(false);
  useEffect(() => {
    setFound(null);
    if (!typedId || saved.some((payee) => payee.target.walletId === typedId)) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      setLooking(true);
      api<{ exists: boolean; name: string | null }>('/payments/resolve', {
        method: 'POST',
        body: JSON.stringify({ walletId: typedId }),
      })
        .then((info) => !cancelled && setFound({ walletId: typedId, name: info.name, exists: info.exists }))
        .catch(() => !cancelled && setFound(null))
        .finally(() => !cancelled && setLooking(false));
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      setLooking(false);
    };
  }, [typedId, saved]);

  const noMatch = !typedId && !searching && searched === text && text.length >= 2 && results.length === 0;

  return (
    <div className="space-y-6">
      <h3 className="font-heading text-lg font-semibold text-foreground-950">Who are you paying?</h3>

      <div>
        <label htmlFor="flow-wallet-search" className="text-xs font-label font-medium text-foreground-500">
          Find a creator
        </label>
        <div className="relative mt-1.5">
          <span className="pointer-events-none absolute left-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-base text-foreground-400">
            <i className={searching || looking ? 'ri-loader-4-line animate-spin' : 'ri-search-line'} />
          </span>
          <input
            id="flow-wallet-search"
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Type a name or email, e.g. Victor"
            className="h-10 w-full rounded-full border border-background-300 bg-background-50 pl-10 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
        <p className="mt-1.5 text-xs text-foreground-500">
          Creators on your campaigns who have a Twiva Pay wallet. Pick one and their wallet is filled in for you.
        </p>

        {results.length > 0 ? (
          <ul className="mt-3 divide-y divide-background-100 overflow-hidden rounded-md border border-background-200">
            {results.map((payee) => (
              <li key={payee.id}>
                <button
                  type="button"
                  onClick={() => onPickPayee(payee)}
                  className="flex w-full cursor-pointer items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-background-100"
                >
                  <Avatar src={payee.avatar} alt={payee.name} size="sm" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground-950">{payee.name}</span>
                    <span className="block truncate text-xs text-foreground-500">
                      {[payee.handle, `Twiva Pay wallet ${payee.target.walletId}`].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs font-medium text-primary-700">Pay</span>
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {text.length === 1 ? (
          <p className="mt-3 text-xs text-foreground-500">Keep typing: at least 2 letters.</p>
        ) : searching && results.length === 0 ? (
          <p className="mt-3 text-xs text-foreground-500">Searching for “{text}”…</p>
        ) : noMatch ? (
          <p className="mt-3 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-xs text-foreground-600">
            No creator matching “{text}”. Only creators on your campaigns with an active Twiva Pay wallet show up
            here. Check the spelling, or enter their wallet ID below.
          </p>
        ) : null}

        {looking ? (
          <p className="mt-3 text-xs text-foreground-500">Looking up wallet {typedId}…</p>
        ) : found && !found.exists ? (
          <p className="mt-3 text-xs text-foreground-500">No Twiva wallet has the ID {found.walletId}.</p>
        ) : found ? (
          <button
            type="button"
            onClick={() => onPickWallet(found.walletId, found.name ?? '')}
            className="mt-3 flex w-full cursor-pointer items-center gap-3 rounded-md border border-background-200 px-4 py-2.5 text-left transition-colors hover:bg-background-100"
          >
            <Avatar src="" alt={found.name ?? 'Wallet holder'} size="sm" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-foreground-950">
                {found.name || 'Unnamed wallet holder'}
              </span>
              <span className="block text-xs text-foreground-500">Twiva Pay wallet {found.walletId}</span>
            </span>
            <span className="shrink-0 text-xs font-medium text-primary-700">Pay</span>
            <i className="ri-arrow-right-s-line text-foreground-400" />
          </button>
        ) : null}
      </div>

      <div className="border-t border-background-100 pt-5">
        <p className="text-xs font-label font-medium text-foreground-500">Or enter a Twiva wallet ID</p>
        <div className="mt-1 flex items-end gap-3">
          <div className="flex-1">
            <label htmlFor="flow-wallet" className="sr-only">
              Twiva wallet ID
            </label>
            <input
              id="flow-wallet"
              value={value}
              inputMode="numeric"
              onChange={(event) => onChange(event.target.value.replace(/\D/g, ''))}
              placeholder="e.g. 2314"
              className="h-9 w-full border-b border-background-300 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-500 focus:outline-none"
            />
          </div>
          <Button size="sm" onClick={onContinue} disabled={!canContinue || checking} className="mb-0.5">
            {checking ? 'Checking…' : 'Continue'}
          </Button>
        </div>
        {error ? (
          <p role="alert" className="mt-2 flex items-center gap-1.5 text-xs text-primary-700">
            <i className="ri-error-warning-line" />
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
